'use client'

import { type FC, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import { IconCaretDown, IconCaretUp } from '@redteclab/icons'

import { Button } from 'base-ui'

import { PRODUCT_SEARCH_FACET } from '../../config/PRODUCT_SEARCH_FACET'
import { ProductSearchRefinementCategoryList } from '../ProductSearchRefinementCategoryList'
import { ProductSearchRefinementCheckbox } from '../ProductSearchRefinementCheckbox'
import { ProductSearchRefinementClearAllRefinements } from '../ProductSearchRefinementClearAllRefinements'
import { ProductSearchRefinementDiscountRange } from '../ProductSearchRefinementDiscountRange'
import { ProductSearchRefinementPrescriptionTypes } from '../ProductSearchRefinementPrescriptionTypes'
import { ProductSearchRefinementPriceRange } from '../ProductSearchRefinementPriceRange'
import { ProductSearchRefinementRating } from '../ProductSearchRefinementRating'
import { ProductSearchRefinementShippingOptions } from '../ProductSearchRefinementShippingOptions'
import { useProductSearchRefinementStateContext } from '../ProductSearchRefinementStateContext'

const MIN_VISIBLE_FILTER_COUNT = 3

/**
 * Define an own list of facets to display instead of using `useDynamicWidgets` from Algolia
 * and rely on Algolia Dashboard https://www.algolia.com/doc/guides/building-search-ui/ui-and-ux-patterns/facet-display/js/#manage-facet-display-from-the-algolia-dashboard.
 * It avoids making two requests to Algolia servers caused by `useDynamicWidgets` handling logic https://github.com/algolia/instantsearch/blob/8e1012f26562e656867d08f140adccf2fddff4b1/packages/react-instantsearch-nextjs/src/InitializePromise.ts#L86-L110.
 *
 * The case with `DynamicWidgets` is mentioned in docs https://www.algolia.com/doc/api-reference/widgets/dynamic-facets/react/#about:
 *
 * ```
 * All matching widgets mount after the first network request completes. To avoid a second network request, facets are set to ['*'] and maxValuesPerFacet is set to 20 by default.
 *
 * If this behavior isn’t what you want, it can be overridden using the facets and maxValuesPerFacet parameters.
 *
 * When server-rendering, InstantSearch will render in two passes, ensuring that the refinements seen on the frontend are correct.
 * ```
 */
const facetOrder: PRODUCT_SEARCH_FACET[] = [
  PRODUCT_SEARCH_FACET.PRIMARY_CATEGORY,
  PRODUCT_SEARCH_FACET.PHARMA_FORM,
  PRODUCT_SEARCH_FACET.PRICE,
  PRODUCT_SEARCH_FACET.AVERAGE_RATING,
  PRODUCT_SEARCH_FACET.BRAND_SEARCH,
  PRODUCT_SEARCH_FACET.VARIANTS_PACK_SIZE,
  PRODUCT_SEARCH_FACET.ACTIVE_SUBSTANCES,
  PRODUCT_SEARCH_FACET.FILTER_ATTRIBUTES,
  PRODUCT_SEARCH_FACET.PRESCRIPTION_MEDICINE,
  PRODUCT_SEARCH_FACET.DISCOUNT_IN_PERCENT,
  PRODUCT_SEARCH_FACET.NOW_PRODUCT_GROUP,
  PRODUCT_SEARCH_FACET.MANUFACTURER,
  PRODUCT_SEARCH_FACET.HAIR_TYPE_MULTI,
  PRODUCT_SEARCH_FACET.SKIN_TYPE_MULTI,
  PRODUCT_SEARCH_FACET.POTENCY,
  PRODUCT_SEARCH_FACET.UV_PROTECTION,
  PRODUCT_SEARCH_FACET.BOOK_LANGUAGE,
  PRODUCT_SEARCH_FACET.AUTHORS,
  PRODUCT_SEARCH_FACET.ABSORBENCY,
  PRODUCT_SEARCH_FACET.LENS_TYPES,
  PRODUCT_SEARCH_FACET.ANIMAL_SPECIES,
  PRODUCT_SEARCH_FACET.VOLTAGE,
  PRODUCT_SEARCH_FACET.SCHUESSLER_SALTS,
  PRODUCT_SEARCH_FACET.RECOMMENDED_AGE,
  PRODUCT_SEARCH_FACET.HAIR_COLOR,
  PRODUCT_SEARCH_FACET.HAIR_LENGTH,
  PRODUCT_SEARCH_FACET.HAIRCUT,
  PRODUCT_SEARCH_FACET.HEAD_CIRCUMFERENCE,
  PRODUCT_SEARCH_FACET.VARIETY,
  PRODUCT_SEARCH_FACET.LIFE_STAGE,
  PRODUCT_SEARCH_FACET.SPECIAL_NEEDS,
  PRODUCT_SEARCH_FACET.PUBLISHER,
  PRODUCT_SEARCH_FACET.CERTIFICATES,
  PRODUCT_SEARCH_FACET.SPORTS,
  PRODUCT_SEARCH_FACET.TEXTURE,
  PRODUCT_SEARCH_FACET.PRODUCT_TYPE_FILTER,
  PRODUCT_SEARCH_FACET.MINERAL_AND_TRACE_ELEMENTS,
  PRODUCT_SEARCH_FACET.VITAMINS,
  PRODUCT_SEARCH_FACET.DIVISIBILITY,
  PRODUCT_SEARCH_FACET.APPLICATION_AREAS,
]

const attributeComponentMap = new Map<
  PRODUCT_SEARCH_FACET,
  FC<{ attribute: string; hidden: boolean }>
>([
  [PRODUCT_SEARCH_FACET.AVERAGE_RATING, ProductSearchRefinementRating],
  [
    PRODUCT_SEARCH_FACET.DISCOUNT_IN_PERCENT,
    ProductSearchRefinementDiscountRange,
  ],
  [
    PRODUCT_SEARCH_FACET.NOW_PRODUCT_GROUP,
    ProductSearchRefinementShippingOptions,
  ],
  [
    PRODUCT_SEARCH_FACET.PRESCRIPTION_MEDICINE,
    ProductSearchRefinementPrescriptionTypes,
  ],
  [PRODUCT_SEARCH_FACET.PRICE, ProductSearchRefinementPriceRange],
  [PRODUCT_SEARCH_FACET.PRIMARY_CATEGORY, ProductSearchRefinementCategoryList],
])

export const ProductSearchRefinements: FC = () => {
  const {
    state: { isMobile },
  } = useProductSearchRefinementStateContext()

  const [isFiltersContainerCropped, setIsFiltersContainerCropped] =
    useState(true)

  return (
    <>
      {facetOrder.map((attribute, index) => {
        const hidden =
          !isMobile &&
          isFiltersContainerCropped &&
          index >= MIN_VISIBLE_FILTER_COUNT

        const facet = Object.values(PRODUCT_SEARCH_FACET).find(
          (item) => item === attribute,
        )

        const RefinementComponent =
          (facet && attributeComponentMap.get(facet)) ??
          ProductSearchRefinementCheckbox

        return (
          <RefinementComponent
            attribute={attribute}
            hidden={hidden}
            key={attribute}
          />
        )
      })}

      {isMobile ? null : (
        <div className="mt-2 flex items-center justify-between">
          <Button
            className="mx-0 px-0"
            data-qa-id={
              isFiltersContainerCropped
                ? 'form-product-filters-unfold'
                : 'form-product-filters-fold'
            }
            onClick={(): void => {
              setIsFiltersContainerCropped(!isFiltersContainerCropped)
            }}
            size="medium"
            startIcon={
              isFiltersContainerCropped ? <IconCaretDown /> : <IconCaretUp />
            }
            variant="text"
          >
            <span className="font-medium">
              {isFiltersContainerCropped ? (
                <FormattedMessage id="searchResults.label.showMore" />
              ) : (
                <FormattedMessage id="searchResults.label.showLess" />
              )}
            </span>
          </Button>
          <ProductSearchRefinementClearAllRefinements />
        </div>
      )}
    </>
  )
}
